import { Center, Spinner } from "@chakra-ui/react";
import ComponentFrameContainer from "components/ComponentFrameContainer";
import { useContextPreviewBlackboard } from "components/containers/inspectionDetail/context/PreviewBlackboard";
import {
  FieldRegister,
  registers,
} from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/containers/selections/setup";
import { SOURCE } from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/context/pinLayoutFrameReducer";
import { useFormState } from "components/containers/inspectionSetting/containers/templateBuilder/context/FormStateContext/context";
import { types } from "components/containers/inspectionSetting/containers/templateBuilder/setup/define";
import { BlackboardData, BlackboardFrame } from "interfaces/models/blackboard";
import { PinDetail } from "interfaces/models/pin";
import {
  getInstancePropertyValueByName,
  getPropertyNameFromSourceBimId,
} from "models/inspection-instances";
import { memo, useCallback, useMemo } from "react";
import { arrayToObject } from "utils/object";
import BlackboardContentText from "../../BlackboardContentText";

export interface BlackboardFrameProps {
  frames: BlackboardFrame[];
  blackboardData: BlackboardData | undefined;
  pinDetail: PinDetail | undefined | null;
  zoomRatio: number;
  isModeEditor?: boolean;
  defaultFormValue?: Record<string, any>;
}

const mapGetContentPreviewBySource = arrayToObject(registers, "source");

const BlackboardFrames = (props: BlackboardFrameProps) => {
  const {
    isModeEditor = false,
    frames,
    pinDetail,
    zoomRatio,
    defaultFormValue,
  } = props;
  const { itemResults, mapInstanceProperty, isLoadingPropertyInstance } =
    useContextPreviewBlackboard();
  const { formValue: _formValue } = useFormState();

  const formValue = defaultFormValue ?? _formValue;

  const mapItemResult = useMemo(
    () => (itemResults ? arrayToObject(itemResults, "id") : {}),
    [itemResults]
  );

  const getContentFrame = useCallback(
    (frame: BlackboardFrame) => {
      const linkedData = frame.linkedData;
      if (!linkedData) {
        return;
      }

      const fields = mapGetContentPreviewBySource[linkedData.source]
        .fields as FieldRegister[];
      const itemResult = mapItemResult[linkedData.key];
      const pinContent = pinDetail?.pinContents?.find(
        (content) => content?.itemResultId === itemResult?.id
      );

      switch (linkedData.source) {
        case SOURCE.ASSET:
          return;

        case SOURCE.BIM: {
          if (!isModeEditor && !mapInstanceProperty) {
            return (
              <Center opacity="0.4" bg="white" position="absolute" inset="0px">
                <Spinner size="xs" color="blue.500" />
              </Center>
            );
          }

          const externalId = pinDetail?.objects?.at(0) || "";
          const categoryName = String(linkedData.extraId);
          const propertyName = getPropertyNameFromSourceBimId(
            linkedData.key,
            categoryName
          );

          const propertyValue = getInstancePropertyValueByName(
            mapInstanceProperty?.get?.(externalId),
            propertyName,
            categoryName
          );

          return (
            <BlackboardContentText
              value={propertyValue || ""}
              style={frame.style}
              zoomRatio={zoomRatio}
            />
          );
        }

        case SOURCE.INSPECTION_FORM: {
          const dataType = itemResult?.dataType;
          const fieldForm = types?.[dataType]?.getBlackboardContent?.({
            pinDetail,
            formData: formValue?.[itemResult?.id || ""],
            style: frame.style,
            itemResult,
            pinContent,
            zoomRatio,
            linkedData,
            mapInstanceProperty,
          });

          return fieldForm;
        }

        default: {
          const fieldCommon = fields?.find(
            (item) => item.id === linkedData.key
          );

          return fieldCommon?.getBlackboardContent?.({
            pinDetail,
            style: frame.style,
            itemResult,
            pinContent,
            zoomRatio,
            linkedData,
            mapInstanceProperty,
          });
        }
      }
    },
    [
      zoomRatio,
      formValue,
      pinDetail,
      mapItemResult,
      isModeEditor,
      mapInstanceProperty,
    ]
  );

  if ((!mapInstanceProperty || isLoadingPropertyInstance) && isModeEditor) {
    return (
      <Center opacity="0.4" bg="white" position="absolute" inset="0px">
        <Spinner size="xs" color="blue.500" />
      </Center>
    );
  }

  return (
    <>
      {frames?.map((frame) => (
        <ComponentFrameContainer
          key={frame.id}
          width={frame.width}
          height={frame.height}
          top={frame.top}
          left={frame.left}
          zIndex={2}
        >
          {getContentFrame(frame)}
        </ComponentFrameContainer>
      ))}
    </>
  );
};

export default memo(BlackboardFrames);
